import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Download, Sparkles } from 'lucide-react';
import { openLink } from '@tma.js/sdk-react';

import { useHaptics } from '@/hooks/useHaptics';
import { useResolveCrTagToSub } from '@/hooks/useResolveCrTagToSub';
import {
  useBattlesQuery,
  useProfileMeQuery,
  useCoachBattleMutation,
  useMeQuery,
} from '@/services/qbotApi';
import { DeckGrid } from '@/components/cr/DeckGrid';
import { Paywall } from '@/components/cr/Paywall';
import { coachAnalysisError } from '@/components/cr/coachError';
import { PlayerSocialButtons } from '@/components/social/PlayerSocialButtons';
import { crDeckImportUrl } from '@/lib/crDeckLink';
import { cn } from '@/lib/cn';
import type { CRBattle, CRBattlePlayer } from '@/types/qbot';

/**
 * State handed over by `<BattleRow>` when the user taps a row — lets the
 * detail render instantly without a refetch. Absent on a cold deep-link
 * (refresh / shared URL), in which case we refetch the 25-entry window and
 * index into it by `:idx`.
 */
interface BattleDetailState {
  battle?: CRBattle;
  playerTag?: string;
}

/** CR battleTime format: "20260520T214500.000Z" → Date (manual parse). */
function parseBattleTime(iso: string): Date | null {
  const m = iso.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/);
  if (!m) return null;
  return new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z`);
}

/**
 * One battle in full: result + crowns + trophy change, arena/mode/time, and
 * BOTH decks (yours + opponent) each with an in-game Import deep link. The
 * opponent block also offers "View CR profile" → `/cr/player/:tag`.
 *
 * The battles window is the same fixed 25-entry CR API window the list shows,
 * so `:idx` is an absolute index into `page=1, pageSize=25`. We prefer the
 * battle passed via router state (instant); the fetch is the deep-link
 * fallback only.
 */
export function CRBattleDetail() {
  const { t, i18n } = useTranslation('cr');
  const { idx } = useParams();
  const { state } = useLocation() as { state: BattleDetailState | null };

  const stateBattle = state?.battle;

  // Tag drives POV resolution and the fallback fetch. From router state when
  // available, else from /profile/me (skipped when state already has the tag).
  const { data: profile } = useProfileMeQuery(undefined, { skip: Boolean(state?.playerTag) });
  const playerTag =
    state?.playerTag ?? profile?.savedTag ?? profile?.player?.tag?.replace('#', '') ?? '';

  const numericIdx = Number(idx);
  const { data, isFetching } = useBattlesQuery(
    { tag: playerTag, page: 1, pageSize: 25 },
    { skip: Boolean(stateBattle) || !playerTag },
  );

  const battle = stateBattle ?? data?.battles?.[numericIdx];

  if (!battle) {
    const loading = !stateBattle && (isFetching || !playerTag || !data);
    return (
      <div className="px-4 py-6 text-sm text-fg-muted">
        {loading ? t('battleDetail.loading') : t('battleDetail.notFound')}
      </div>
    );
  }

  // POV resolution mirrors <BattleRow>: team[0] is normally the queried player,
  // but flip defensively if the tag matches the opponent side.
  const normalisedTag = `#${playerTag.replace(/^#/, '').toUpperCase()}`;
  const isTeam0 = battle.team[0]?.tag?.toUpperCase() === normalisedTag;
  const me = isTeam0 ? battle.team[0] : battle.opponent[0];
  const opp = isTeam0 ? battle.opponent[0] : battle.team[0];
  if (!me || !opp) {
    return <div className="px-4 py-6 text-sm text-fg-muted">{t('battleDetail.notFound')}</div>;
  }

  const won = me.crowns > opp.crowns;
  const draw = me.crowns === opp.crowns;
  const resultLabel = draw
    ? t('battleDetail.result.draw')
    : won
      ? t('battleDetail.result.won')
      : t('battleDetail.result.lost');
  const resultClass = draw
    ? 'bg-amber-500/20 text-amber-300'
    : won
      ? 'bg-emerald-500/20 text-emerald-300'
      : 'bg-rose-500/20 text-rose-300';

  const date = parseBattleTime(battle.battleTime);
  const timeLabel = date
    ? date.toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })
    : '';

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto px-4 py-4">
      <header className="flex flex-col items-center gap-1 rounded-xl bg-bg-secondary p-4 text-center">
        <span
          className={cn(
            'rounded px-3 py-0.5 text-xs font-bold uppercase tracking-wide',
            resultClass,
          )}
        >
          {resultLabel}
        </span>
        <div className="mt-1 flex items-center gap-3 text-3xl font-bold tabular-nums text-fg">
          <span>{me.crowns}</span>
          <span className="text-fg-muted">–</span>
          <span>{opp.crowns}</span>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-[11px] text-fg-muted">
          <span>{battle.gameMode?.name ?? battle.type}</span>
          {battle.arena?.name && (
            <>
              <span aria-hidden>·</span>
              <span>{battle.arena.name}</span>
            </>
          )}
          {timeLabel && (
            <>
              <span aria-hidden>·</span>
              <span>{timeLabel}</span>
            </>
          )}
        </div>
        {typeof me.trophyChange === 'number' && me.trophyChange !== 0 && (
          <span
            className={cn(
              'text-xs font-semibold tabular-nums',
              me.trophyChange > 0 ? 'text-emerald-300' : 'text-rose-300',
            )}
          >
            {me.trophyChange > 0 ? '+' : ''}
            {me.trophyChange} 🏆
          </span>
        )}
      </header>

      <DeckSection heading={t('battleDetail.you')} player={me} />
      <DeckSection
        heading={t('battleDetail.opponent')}
        player={opp}
        viewProfileTo={opp.tag ? `/cr/player/${encodeURIComponent(opp.tag)}` : undefined}
        socialTag={opp.tag}
      />

      <BattleAnalysisSection tag={playerTag} battleTime={battle.battleTime} />
    </div>
  );
}

/**
 * Embedded post-battle (result) analysis, Pro-gated. Unlike the old matchup
 * analysis (which compared two CURRENT decks pre-game), this asks the AI about
 * THIS finished battle: the server looks it up by `battleTime` and feeds the
 * AI the actually-played decks + crowns + tower HP + trophy/elixir, so it
 * speaks in past tense (what went well / wrong). Free tier sees the Paywall.
 */
function BattleAnalysisSection({ tag, battleTime }: { tag: string; battleTime: string }) {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const { data: identity } = useMeQuery();
  const [analyse, { data, isLoading, error }] = useCoachBattleMutation();

  const isPro = identity?.tier === 'pro' || identity?.tier === 'admin';
  // Need a tag to look up the battle log and a battleTime to pick the match.
  if (!tag || !battleTime) return null;

  const onAnalyse = () => {
    haptics.impact('medium');
    analyse({ tag: tag.replace(/^#/, ''), battleTime }).catch(() => undefined);
  };

  if (!isPro) {
    return <Paywall feature={t('battleAnalysis.cta')} />;
  }

  const a = data?.analysis;
  return (
    <section className="flex flex-col gap-3">
      {!a && (
        <button
          type="button"
          onClick={onAnalyse}
          disabled={isLoading}
          className="flex items-center justify-center gap-2 rounded-lg bg-brand py-3 text-sm font-bold uppercase tracking-wide text-brand-fg transition-opacity active:opacity-80 disabled:opacity-50"
        >
          <Sparkles className={isLoading ? 'h-4 w-4 animate-pulse' : 'h-4 w-4'} />
          {isLoading ? t('battleAnalysis.thinking') : t('battleAnalysis.cta')}
        </button>
      )}
      {!!error && (
        <p className="rounded-md bg-rose-500/10 p-3 text-sm text-rose-300">
          {coachAnalysisError(error, t, 'analysis')}
        </p>
      )}
      {a && (
        <div className="flex flex-col gap-3">
          <BattleBlock title={t('battleAnalysis.section.summary')} body={a.summary} />
          <BattleBlock title={t('battleAnalysis.section.whatWentWell')} body={a.whatWentWell} />
          <BattleBlock title={t('battleAnalysis.section.whatWentWrong')} body={a.whatWentWrong} />
          <BattleBlock
            title={t('battleAnalysis.section.keyInteractions')}
            body={a.keyInteractions}
          />
          <BattleBlock title={t('battleAnalysis.section.improve')} body={a.improve} />
        </div>
      )}
    </section>
  );
}

function BattleBlock({ title, body }: { title: string; body: string }) {
  if (!body) return null;
  return (
    <div className="rounded-xl bg-bg-secondary p-3">
      <h2 className="mb-1 text-xs font-bold uppercase tracking-wider text-brand">{title}</h2>
      <p className="whitespace-pre-line text-sm leading-relaxed text-fg">{body}</p>
    </div>
  );
}

interface DeckSectionProps {
  heading: string;
  player: CRBattlePlayer;
  /** When set, render a "View CR profile" link to this route. */
  viewProfileTo?: string;
  /**
   * When set (opponent block), resolve this CR tag → Logto `sub` (QBot first,
   * qduels fallback) and, if the player is mapped, render follow/add-friend
   * buttons alongside "View CR profile". Omitted for the "you" block.
   */
  socialTag?: string;
}

function DeckSection({ heading, player, viewProfileTo, socialTag }: DeckSectionProps) {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();

  // Identity bridge (CR side): a battle opponent is known only by CR tag. We
  // resolve it to a Logto sub — QBot primary, qduels fallback. Only resolved
  // players get the social buttons; an unresolved tag yields `undefined` → no
  // buttons, and no further social API calls fire.
  const { logtoUserId: socialUserId } = useResolveCrTagToSub(socialTag);

  const handleImport = () => {
    haptics.impact('light');
    openLink.ifAvailable(crDeckImportUrl(player.cards));
  };

  return (
    <section className="rounded-xl bg-bg-secondary p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wider text-fg-muted">{heading}</p>
          <p className="truncate text-sm font-semibold text-fg">{player.name}</p>
        </div>
        {player.cards?.length > 0 && (
          <button
            type="button"
            onClick={handleImport}
            className="flex shrink-0 items-center gap-1 rounded-md bg-bg-tertiary px-2.5 py-1 text-[11px] font-semibold text-brand transition-opacity active:opacity-80"
          >
            <Download className="h-3.5 w-3.5" />
            {t('battleDetail.import')}
          </button>
        )}
      </div>

      {player.cards?.length > 0 && <DeckGrid cards={player.cards} showLevels={false} />}

      {socialUserId && (
        <div className="mt-3">
          <PlayerSocialButtons userId={socialUserId} interactive />
        </div>
      )}

      {viewProfileTo && (
        <Link
          to={viewProfileTo}
          className="mt-3 flex items-center justify-between rounded-lg bg-bg-tertiary px-3 py-2 text-xs font-semibold text-brand transition-opacity active:opacity-80"
        >
          {t('battleDetail.viewProfile')}
          <ChevronRight aria-hidden className="h-4 w-4" />
        </Link>
      )}
    </section>
  );
}
